import {
  ArrowRight,
  Check,
  CircleCheckBig,
  Cloud,
  Cpu,
  KeyRound,
  RefreshCw,
  Server,
  UserRound,
  type LucideIcon,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useApp } from "@/lib/AppContext";
import type { SetupStep } from "@/lib/api";
import { cn } from "@/lib/cn";

/**
 * UI-13's Setup section, and the increment's real deliverable: what still stops a
 * recording running end to end.
 *
 * Nothing here is stored. Every row is derived on the Server at the moment it is asked
 * for — is a worker heartbeating, is every placed model actually on one, does every
 * cloud placement have a key — because a checklist that remembers being ticked goes
 * green on a machine that has since been switched off, on a model that has since been
 * deleted, on a key that has since been revoked. The one moment this page is read is the
 * moment somebody is asking why nothing is happening.
 *
 * The same answer drives the warning dot on the rail's settings button, from the same
 * poll, so the dot and this page can never disagree.
 */
export function SetupSettings({ summary }: { summary: string }) {
  const { setup, refreshSetup } = useApp();

  if (!setup) {
    return (
      <Frame summary={summary}>
        <p className="text-[length:var(--text-sm)] text-[var(--text-secondary)]">Checking…</p>
      </Frame>
    );
  }

  const blocking = setup.blocking > 0;

  return (
    <Frame summary={summary}>
      <section className="rounded-[var(--radius-xl)] border border-[var(--border-subtle)] bg-[var(--surface-glass)] p-[var(--space-6)] backdrop-blur-[var(--blur-sm)]">
        <div className="flex items-start justify-between gap-[var(--space-4)]">
          <div className="min-w-0">
            <h2 className="text-[length:var(--text-lg)] font-semibold text-[var(--text-primary)]">
              Getting Factorium running
            </h2>
            <p className="mt-1 text-[length:var(--text-sm)] text-[var(--text-secondary)]">
              Each step opens the section that finishes it. Nothing here blocks the app — steps that
              are not done simply pause the pipeline stages that depend on them.
            </p>
          </div>

          <div className="flex shrink-0 items-center gap-[var(--space-3)]">
            <span className="font-[family-name:var(--font-mono)] text-[length:var(--text-sm)] text-[var(--text-secondary)]">
              {setup.done} of {setup.total} done
            </span>

            {/* Derived state goes stale on its own — a PC comes back, a download
                finishes — so there is a way to ask again without reloading the app. */}
            <button
              type="button"
              onClick={refreshSetup}
              aria-label="Check again"
              title="Check again"
              className="flex h-7 w-7 items-center justify-center rounded-[var(--radius-md)] border border-[var(--border-default)] text-[var(--text-secondary)] transition-colors hover:text-[var(--text-primary)]"
            >
              <RefreshCw size={13} strokeWidth={2} />
            </button>
          </div>
        </div>

        <div
          role="progressbar"
          aria-valuenow={setup.done}
          aria-valuemin={0}
          aria-valuemax={setup.total}
          className="mt-[var(--space-4)] h-[6px] overflow-hidden rounded-[var(--radius-full)] bg-[var(--surface-2)]"
        >
          <div
            className="h-full rounded-[var(--radius-full)] transition-[width] duration-[var(--duration-slow)]"
            style={{
              width: `${setup.total === 0 ? 100 : (setup.done / setup.total) * 100}%`,
              background: blocking ? "var(--warning)" : "var(--success)",
            }}
          />
        </div>
      </section>

      <ul className="flex flex-col gap-[var(--space-2)]">
        {setup.steps.map((step) => (
          <Row key={step.id} step={step} />
        ))}
      </ul>

      {!blocking && (
        <p className="text-[length:var(--text-xs)] text-[var(--text-muted)]">
          Nothing is in the way of an end-to-end run. Upload a recording and the pipeline takes it
          from there.
        </p>
      )}
    </Frame>
  );
}

function Row({ step }: { step: SetupStep }) {
  const Icon = ICONS[step.id] ?? CircleCheckBig;
  const done = step.state === "done";
  const later = step.state === "later";

  // Uncounted like `later` and actionable like `needs-you` (M11.2): a capture PC is
  // something you could set up now and need not, so the row is quiet but still leads to
  // the section that sets it up.
  const optional = step.state === "optional";
  const quiet = later || optional;

  const body = (
    <>
      <span
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[var(--radius-md)]"
        style={{
          color: done ? "var(--success)" : quiet ? "var(--text-muted)" : "var(--warning)",
          background: `color-mix(in oklch, currentColor 12%, transparent)`,
        }}
      >
        {done ? <Check size={15} strokeWidth={2.5} /> : <Icon size={15} strokeWidth={2} />}
      </span>

      <span className="min-w-0 flex-1">
        <span className="block text-[length:var(--text-sm)] font-semibold text-[var(--text-primary)]">
          {step.label}
        </span>
        <span className="block text-[length:var(--text-xs)] text-[var(--text-secondary)]">
          {step.detail}
        </span>
      </span>

      <span
        className={cn(
          "shrink-0 rounded-[var(--radius-full)] px-[9px] py-[2px] text-[length:var(--text-2xs)] font-semibold",
          done && "bg-[color-mix(in_oklch,var(--success)_14%,transparent)] text-[var(--success)]",
          quiet && "bg-[var(--surface-2)] text-[var(--text-muted)]",
          !done && !quiet && "bg-[color-mix(in_oklch,var(--warning)_14%,transparent)] text-[var(--warning)]",
        )}
      >
        {/* A row nothing can finish yet says which milestone brings it rather than
            sitting on "Needs you" forever — the same honesty as the shell's empty
            rooms. A row that is merely optional says so instead:
            it is not unfinished, and it is not waiting on anybody. */}
        {done ? "Done" : optional ? "Optional" : later ? `Arrives in ${step.milestone}` : "Needs you"}
      </span>

      {!later && <ArrowRight size={14} strokeWidth={2} className="shrink-0 text-[var(--text-muted)]" />}
    </>
  );

  const className = cn(
    "flex w-full items-center gap-[var(--space-4)] rounded-[var(--radius-lg)] border px-[var(--space-4)] py-[var(--space-3)] text-left transition-colors",
    later
      ? "border-[var(--border-subtle)] opacity-70"
      : "border-[var(--border-subtle)] hover:border-[var(--border-default)]",
  );

  // A row that leads somewhere is a link; one that does not is not, rather than a link
  // that goes to a placeholder and says nothing more than the row already did. An
  // `optional` row does lead somewhere — that is what separates it from a `later` one.
  return (
    <li>
      {later || !step.section ? (
        <div className={className}>{body}</div>
      ) : (
        <Link to={`/settings/${step.section}`} className={className}>
          {body}
        </Link>
      )}
    </li>
  );
}

function Frame({ summary, children }: { summary: string; children: React.ReactNode }) {
  return (
    <div className="flex max-w-[860px] flex-col gap-[var(--space-6)] px-[var(--space-8)] py-[var(--space-8)]">
      <header>
        <h1 className="text-[length:var(--text-3xl)] font-extrabold tracking-[var(--tracking-snug)] text-[var(--text-primary)]">
          Setup
        </h1>
        <p className="mt-1 text-[length:var(--text-md)] text-[var(--text-secondary)]">{summary}</p>
      </header>

      {children}
    </div>
  );
}

const ICONS: Record<string, LucideIcon> = {
  "signed-in": UserRound,
  worker: Server,
  engines: Cpu,
  providers: Cloud,
  capture: KeyRound,
};
